import React from 'react';

import Snackbar from 'components/DesignSystem/Snackbar';
import { formatInstantFull } from 'helper/flux/studyDates';
import { Instant } from 'types/Flux/Personalized/myStudies';

// 'HH:MM' no fim de 'dd/mm/aaaa HH:MM' (formatInstantFull).
const TIME_AT_END = /(\d{2}:\d{2})$/;

/**
 * Hora 'HH:MM' (24 h, America/Sao_Paulo) de um epoch em ms. O `studyDates` ainda não expõe só a
 * hora e a #1998 mexe nele nesta onda, então reaproveita o `formatInstantFull` e pega o fim.
 *
 * @param epochMs - Milissegundos desde a época.
 * @returns 'HH:MM', ou null se o valor faltar ou não for legível.
 */
function formatSaoPauloTime(epochMs: number | undefined): string | null {
  if (epochMs === undefined) return null;
  const date = new Date(epochMs);
  // NaN, Infinity e valores além do alcance do Date dão Invalid Date (toISOString lançaria)
  if (Number.isNaN(date.getTime())) return null;
  return TIME_AT_END.exec(formatInstantFull(date.toISOString() as Instant))?.[1] ?? null;
}

export interface StaleBannerProps {
  /**
   * Momento da última atualização bem-sucedida do que a lista mostra, em milissegundos desde a
   * época: o `lastUpdatedAt` do polling (#2004), o maior entre o `fulfilledTimeStamp` da lista e a
   * última resposta de status da página. A faixa mostra a hora em America/Sao_Paulo, 24 h ("desde
   * 14:31"), seja qual for o fuso de quem abre a tela. Sem valor legível, a faixa omite o "desde
   * HH:MM" em vez de mostrar uma hora errada.
   */
  lastUpdatedAt?: number;
  /** Refaz a consulta ("Tentar de novo"). */
  onRetry: () => void;
}

/**
 * Faixa discreta acima da lista da aba Meus Estudos quando a atualização falha e a lista continua
 * na tela (estado `error-stale` ou falha do polling de status, #2004; .dc.html:452), sobre o
 * `Snackbar` danger do DS. "Tentar de novo"
 * é o texto do design. Vai em `role="alert"`, como o `ListErrorBlock`: a faixa é montada já com o
 * texto, e uma região `status` criada já preenchida costuma não ser lida pelo leitor de tela.
 */
export default function StaleBanner({ lastUpdatedAt, onRetry }: StaleBannerProps) {
  const time = formatSaoPauloTime(lastUpdatedAt);

  return (
    <div role="alert" className="my-studies-stale-banner">
      <Snackbar variant="danger" actionText="Tentar de novo" onAction={onRetry}>
        {time ? `Status não atualizado desde ${time}.` : 'Status não atualizado.'}
      </Snackbar>
    </div>
  );
}
